import { inject, Injectable } from '@angular/core';

import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { BRAND } from '@tellent/types';
import { map, of } from 'rxjs';
import { catchError, distinctUntilChanged, skip, switchMap, tap } from 'rxjs/operators';

import { filterBoolean } from '@recruitee/common';
import { createEffectFactory } from '@recruitee/ngrx';
import { WindowRef } from '@recruitee/user-interface';

import { TellentCurrentUserHttpClient } from './tellent-current-user.http';
import {
  TellentCurrentUserEntityActions,
  TellentCurrentUserSelectors,
} from './tellent-current-user.store';

@Injectable({ providedIn: 'root' })
export class TellentCurrentUserEffects {
  public effects = createEffectFactory(TellentCurrentUserEntityActions, this.actions$);
  public store = inject(Store);
  public brand = inject(BRAND);
  public windowRef = inject(WindowRef);

  public fetchCurrentUser$ = this.effects(TellentCurrentUserEntityActions.fetch, () =>
    this.tellentCurrentUserHttpClient.fetchCurrentUser(),
  );

  public changePassword$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TellentCurrentUserEntityActions.updatePassword),
      switchMap(({ payload }) =>
        this.tellentCurrentUserHttpClient.updatePassword(payload).pipe(
          map(response =>
            TellentCurrentUserEntityActions.updatePasswordSuccess({ payload: response }),
          ),
          catchError(error =>
            of(TellentCurrentUserEntityActions.updatePasswordFail({ payload: error })),
          ),
        ),
      ),
    ),
  );

  public updateCurrentUserData$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TellentCurrentUserEntityActions.updateCurrentUserData),
      switchMap(({ payload }) =>
        this.tellentCurrentUserHttpClient.updateCurrentUserData(payload).pipe(
          map(response =>
            TellentCurrentUserEntityActions.updateCurrentUserDataSuccess({
              payload: response,
            }),
          ),
          catchError(error =>
            of(TellentCurrentUserEntityActions.updateCurrentUserDataFail({ payload: error })),
          ),
        ),
      ),
    ),
  );

  public reloadAfterLanguageChange$ = createEffect(
    () =>
      this.store.select(TellentCurrentUserSelectors.data).pipe(
        filterBoolean,
        map(({ language }) => language),
        distinctUntilChanged(),
        skip(1),
        tap(() => this.windowRef.nativeWindow.location.reload()),
      ),
    { dispatch: false },
  );

  public leaveCompany$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TellentCurrentUserEntityActions.leaveCompany),
      switchMap(({ payload }) =>
        this.tellentCurrentUserHttpClient.leaveCompany(payload.orgGuid).pipe(
          map(response =>
            TellentCurrentUserEntityActions.leaveCompanySuccess({ payload: response }),
          ),
          catchError(error =>
            of(TellentCurrentUserEntityActions.leaveCompanyFail({ payload: error })),
          ),
        ),
      ),
    ),
  );

  constructor(
    private tellentCurrentUserHttpClient: TellentCurrentUserHttpClient,
    private actions$: Actions,
  ) {}
}
